import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  selectedConversation: null,
  conversationType: null,
  messages: [],
  withMessagesConversations: [],
  withMessagesGroups: [],
  isChatModalOpen: false,
  lastAction: null, // Add lastAction to track the type of change
  // Unsent message text, keyed by `${conversationType}:${conversationId}`.
  // Lives here rather than in MessageInput's local state because that
  // component is not remounted when you switch conversations (and is
  // destroyed entirely when the chat modal closes), so per-conversation
  // drafts have to survive both. Being part of this slice also means they
  // are wiped by resetChatState on logout, like every other chat value.
  drafts: {},
};

// Personal and group conversations live in two separate lists; both are
// updated the same way, so the reducers below just pick the right one.
const listKeyFor = (conversationType) =>
  conversationType === "group"
    ? "withMessagesGroups"
    : "withMessagesConversations";

// Conversation ids arrive as both numbers and strings depending on the source
// (REST payload vs SignalR event), so compare them loosely but explicitly.
const sameConversation = (conversation, conversationId) =>
  conversation?.value != null &&
  String(conversation.value) === String(conversationId);

const ChatSlice = createSlice({
  name: "conversation",
  initialState,
  reducers: {
    setSelectedConversation: (state, action) => {
      const { conversation, type } = action.payload;
      state.selectedConversation = conversation;
      state.conversationType = type;
    },
    setMessages: (state, action) => {
      state.messages = action.payload;
    },
    setWithMessagesConversations: (state, action) => {
      state.withMessagesConversations = action.payload;
    },
    setWithMessagesGroups: (state, action) => {
      state.withMessagesGroups = action.payload;
    },
    clearConversation: (state) => {
      state.selectedConversation = null;
      state.messages = [];
      state.isChatModalOpen = false;
    },
    setChatModalOpen: (state, action) => {
      state.isChatModalOpen = action.payload;
    },
    markMessagesAsRead: (state, action) => {
      const { readerId, targetUserId, conversationType, readAt } = action.payload;
      const reader = Number(readerId);

      state.messages = state.messages.map((message) => {
        if (conversationType === "group") {
          if (targetUserId != null && String(message.conversationId) !== String(targetUserId)) return message;
        } else if (String(message.receiverId) !== String(readerId) ||
          (targetUserId != null && String(message.senderId) !== String(targetUserId))) {
          return message;
        }
        const currentTime = readAt || new Date().toISOString();

        if (conversationType === "group") {
          // Handle group chat
          const seenUsers = message.seenUsers || [];

          const existingSeen = seenUsers.find((s) => String(s?.userId ?? s) === String(reader));

          if (existingSeen) {
            // Already read earlier → keep previous timestamp
            return {
              ...message,
              seenUsers: seenUsers.map((s) => (s.userId === reader ? s : s)),
            };
          }

          // Not read earlier → add new readAt
          return {
            ...message,
            seenUsers: [
              ...seenUsers,
              {
                userId: reader,
                readAt: currentTime,
              },
            ],
          };
        } else {
          // Simple one-to-one chat
          if (message.isRead) {
            // Already read → KEEP existing readAt, do NOT overwrite
            return message;
          }

          // First time marking read → set timestamp
          return {
            ...message,
            isRead: true,
            readAt: message.readAt || currentTime, // only set if missing
          };
        }
      });
    },

    // In your ChatSlice
    updateMessage: (state, action) => {
      const { messageId, updatedProperties } = action.payload;
      state.messages = state.messages.map((message) =>
        message.id === messageId
          ? { ...message, ...updatedProperties }
          : message
      );
      state.lastAction = "update";
    },
    resetLastAction: (state) => {
      state.lastAction = null; // Reset lastAction
    },
    // Move one conversation to the top of its list (the sidebar is ordered
    // most-recent-first) and optionally bump its unread badge. Together with
    // clearConversationUnread this replaces the full `users-with-messages`
    // refetch that used to run after every single send/receive/read — the
    // list already holds everything those refetches changed. No-ops when the
    // conversation isn't in the list yet; callers detect that case and fall
    // back to a real refetch, so brand new conversations still appear.
    promoteConversation: (state, action) => {
      const {
        conversationId,
        conversationType,
        incrementUnread = false,
      } = action.payload || {};
      if (conversationId == null) return;

      const list = state[listKeyFor(conversationType)];
      if (!Array.isArray(list)) return;

      const index = list.findIndex((item) =>
        sameConversation(item, conversationId),
      );
      if (index === -1) return;

      const [conversation] = list.splice(index, 1);
      if (incrementUnread) {
        conversation.unreadCount = (conversation.unreadCount || 0) + 1;
      }
      list.unshift(conversation);
    },

    // Zero one conversation's unread badge, after its messages were marked
    // read. Previously this required refetching the whole list.
    clearConversationUnread: (state, action) => {
      const { conversationId, conversationType } = action.payload || {};
      if (conversationId == null) return;

      const list = state[listKeyFor(conversationType)];
      if (!Array.isArray(list)) return;

      const conversation = list.find((item) =>
        sameConversation(item, conversationId),
      );
      if (conversation) conversation.unreadCount = 0;
    },

    // Store (or, for empty text, drop) the unsent draft for one conversation.
    setDraft: (state, action) => {
      const { key, value } = action.payload || {};
      if (!key) return;
      if (value && value.trim()) {
        state.drafts[key] = value;
      } else {
        delete state.drafts[key];
      }
    },
    // Wipes the entire chat slice, including the cached conversation/group
    // lists (`withMessagesConversations`/`withMessagesGroups`). Dispatched
    // on logout (see Header/Index.js's handleLogout) — without this, those
    // lists stay in the in-memory Redux store across a logout, so if a
    // different user logs in on the same device/tab right after, they'd
    // briefly (or, if they never open the conversations list again before
    // it refetches, indefinitely) see the previous user's conversations.
    resetChatState: () => initialState,
  },
});

export const {
  setSelectedConversation,
  setMessages,
  setWithMessagesConversations,
  setWithMessagesGroups,
  clearConversation,
  setChatModalOpen,
  markMessagesAsRead,
  updateMessage,
  resetLastAction,
  resetChatState,
  setDraft,
  promoteConversation,
  clearConversationUnread,
} = ChatSlice.actions;

export default ChatSlice.reducer;
